import { Grid, TextField, Typography, styled } from "@mui/material";
import { useFormik } from "formik";
import { forwardRef, useCallback, useImperativeHandle } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useConfig } from "../utils/useConfig";
import { useDynamicEmployeeValidationSchema } from "../utils/useDynamicValidationSchema";

export const StyledTextField = styled(TextField)<{
  textcolor: string;
  backgroundcolor?: string;
}>(({ textcolor, backgroundcolor }) => ({
  "& .MuiInputBase-root": {
    borderRadius: "15px",
    backgroundColor: backgroundcolor ?? "white",
    height: "55px",
    fontSize: "25px",
    paddingLeft: "16px",
    paddingRight: "16px"
  },
  "& .MuiInputBase-input": {
    color: textcolor,
    fontWeight: 700,
    fontFamily: "GT-Alpina-Condensed"
  },
  "& .MuiFormHelperText-root": {
    fontSize: "14px",
    fontWeight: 600
  },
  "& .MuiInputBase-input.Mui-disabled": {
    color: textcolor, // Ensure text color remains unchanged
    opacity: 1, // Prevents default dimming effect
    WebkitTextFillColor: textcolor // **Overrides the unwanted gray color**
  }
}));

interface ContactFormProps {
  webflowSlug: string;
  color: string;
  headingColor: string;
  onSuccess: () => void;
  vertical?: boolean;
}

export interface ContactFormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export const ContactForm = forwardRef(
  (
    { webflowSlug, headingColor, color, onSuccess, vertical }: ContactFormProps,
    ref
  ) => {
    const { config } = useConfig();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const formik = useFormik({
      initialValues: {
        firstName: "",
        lastName: "",
        email: "",
        phone: ""
      },
      validationSchema: useDynamicEmployeeValidationSchema(),
      onSubmit: async (values) => {
        try {
          const recaptchaToken = await handleReCaptchaVerify();
          if (!recaptchaToken) throw new Error("ReCaptcha Error"); // Not sending recaptcha token to hubspot as not required, just frontend verification/validation

          const formData = new URLSearchParams();
          Object.entries(values).forEach(([key, value]) => {
            formData.append(key, value);
          });

          const hutk =
            document.cookie
              .split("; ")
              .find((row) => row.startsWith("hubspotutk="))
              ?.split("=")[1] || "";

          const hsContext = {
            hutk,
            pageUrl: `https://www.extraordinarypay.com/${webflowSlug}`,
            pageName: webflowSlug
          };

          formData.append("hs_context", JSON.stringify(hsContext));

          const response = await fetch(
            `https://forms.hubspot.com/uploads/form/v2/${config?.HUBSPOT_PORTAL_ID}/${config?.HUBSPOT_FORM_ID}`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/x-www-form-urlencoded"
              },
              body: formData.toString()
            }
          );

          if (response.ok) {
            console.log("Submission success");
          } else {
            console.error("Error submitting:", await response.text());
          }
        } catch (error) {
          console.error("Error:", error);
        }
        onSuccess();
      },
      enableReinitialize: true
    });

    useImperativeHandle(ref, () => ({
      ...formik
    }));

    const handleReCaptchaVerify = useCallback(async () => {
      if (!executeRecaptcha) {
        console.log("Execute recaptcha not yet available");
        return;
      }

      return await executeRecaptcha("calculators-contact-form");
    }, [executeRecaptcha]);

    return (
      <>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "20px",
            color: `${headingColor} !important`,
            my: 2
          }}
        >
          Enter your details to view your results
        </Typography>

        <Grid
          container
          justifyContent="center"
          alignItems="center"
          textAlign="center"
          spacing={2}
        >
          <Grid item xs={vertical ? 12 : 6}>
            <StyledTextField
              textcolor={color}
              variant="standard"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              name="firstName"
              placeholder="First Name*"
              fullWidth
              size="small"
              error={
                formik.touched.firstName && Boolean(formik.errors.firstName)
              }
              helperText={formik.touched.firstName && formik.errors.firstName}
              InputProps={{
                disableUnderline: true
              }}
            />
          </Grid>

          <Grid item xs={vertical ? 12 : 6}>
            <StyledTextField
              textcolor={color}
              variant="standard"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              name="lastName"
              placeholder="Last Name*"
              fullWidth
              size="small"
              error={formik.touched.lastName && Boolean(formik.errors.lastName)}
              helperText={formik.touched.lastName && formik.errors.lastName}
              InputProps={{
                disableUnderline: true
              }}
            />
          </Grid>

          <Grid item xs={vertical ? 12 : 6}>
            <StyledTextField
              textcolor={color}
              variant="standard"
              value={formik.values.email}
              onChange={formik.handleChange}
              name="email"
              placeholder="Email*"
              fullWidth
              size="small"
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              InputProps={{
                disableUnderline: true
              }}
            />
          </Grid>

          <Grid item xs={vertical ? 12 : 6}>
            <StyledTextField
              textcolor={color}
              variant="standard"
              value={formik.values.phone}
              onChange={formik.handleChange}
              name="phone"
              placeholder="Phone*"
              fullWidth
              size="small"
              error={formik.touched.phone && Boolean(formik.errors.phone)}
              helperText={formik.touched.phone && formik.errors.phone}
              InputProps={{
                disableUnderline: true
              }}
            />
          </Grid>
        </Grid>
      </>
    );
  }
);
